import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { useQuery, UseQueryResult } from 'react-query';
import http from '../config/http-service';
import { masterProductsEndpoint } from './use-get-master-products-api';

// GET ums/v1/cms/master-products/uom-options — the unit-of-measure vocabulary for
// every UOM dropdown (master-product baseUnit/itemUnit/packUnit, supplier defaultUnit).
// The list lives in BE code, so it changes only on deploy: staleTime Infinity means one
// fetch per session, which is fresher than any TTL a page reload would beat anyway.
//
// Retries stay at the React Query default, unlike the attribute dictionary hook's
// retry:false. The dictionary is an enhancement query; this one is not — baseUnit is
// REQUIRED, so an empty list blocks master-product creation outright rather than
// merely degrading a form.
const endpoint = `${masterProductsEndpoint}/uom-options`;

// Stable fallback: `data ?? []` would allocate a new array every render, and these
// options feed useMemo/useEffect dependency arrays. Always fall back to this.
// `readonly` so a shared module-level array can't be mutated by a consumer;
// buildCanonicalOptions/toCanonicalValue/bareSelect all take `readonly string[]`.
export const EMPTY_UOM_OPTIONS: readonly string[] = [];

export const useGetUomOptionsApi = (): UseQueryResult<string[], unknown> => {
  const isLoggedIn = useIsLoggedIn();
  return useQuery({
    queryKey: [endpoint],
    queryFn: async (): Promise<string[]> => {
      const { data } = await http.get(endpoint);
      return data?.data ?? [];
    },
    staleTime: Infinity,
    enabled: isLoggedIn,
  });
};
